import { useEffect, useState } from "react";
import { Activity, BellRing, Building2, ChevronRight, Gavel, ListTodo, Users, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { usePushNotifications } from "../../lib/use-push-notifications";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type RecentTask = {
  id: string;
  title: string;
  taskType: string;
  module: string;
  status: string;
  createdAt: string;
};

type DashboardOverview = {
  organizations: number;
  users: number;
  activeAuctions: number;
  openTasks: number;
  pendingOrgRegistrations: number;
  pendingAuctionApprovals: number;
  openSupportTickets: number;
  recentTasks: RecentTask[];
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
}

function taskTypeLabel(taskType: string, module: string) {
  if (taskType === "approval_review" && module === "auctions") return "Auksjonsgodkjenning";
  if (taskType === "org_registration") return "Ny registrering";
  if (taskType === "org_registration_duplicate") return "Allerede kunde";
  if (taskType === "org_approval") return "Org-godkjenning";
  if (taskType === "user_review") return "Brukergjennomgang";
  return taskType;
}

const PUSH_BANNER_DISMISSED_KEY = "push-banner-dismissed";

function isInstalledPwa() {
  return window.matchMedia("(display-mode: standalone)").matches
    || ("standalone" in navigator && (navigator as unknown as { standalone: boolean }).standalone === true);
}

function PushNotificationBanner() {
  const push = usePushNotifications();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(PUSH_BANNER_DISMISSED_KEY) === "1");
  const [isPwa, setIsPwa] = useState(false);

  useEffect(() => {
    setIsPwa(isInstalledPwa());
  }, []);

  if (!isPwa || dismissed || push.state === "loading" || push.state === "unsupported" || push.state === "subscribed") {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(PUSH_BANNER_DISMISSED_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="relative flex items-center gap-4 rounded-2xl border border-accent/20 bg-accent/5 px-5 py-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10">
        <BellRing className="h-5 w-5 text-accent" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-[#1a2e35]">Aktiver push-varsler</p>
        <p className="text-sm text-muted-foreground">
          {push.state === "denied"
            ? "Push-varsler er blokkert. Endre tillatelser i nettleserinnstillingene."
            : "Få beskjed med én gang når nye oppgaver kommer inn."}
        </p>
      </div>
      {push.state !== "denied" && (
        <Button
          type="button"
          onClick={() => void push.subscribe()}
          className="shrink-0 rounded-xl text-sm font-bold"
        >
          Aktiver
        </Button>
      )}
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute right-2 top-2 rounded-lg p-1 text-muted-foreground/60 hover:text-muted-foreground"
        aria-label="Lukk"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/admin/overview`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setOverview(data); })
              .catch(() => { /* network error – silently handled */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const stats = [
    { label: "Organisasjoner", value: overview?.organizations, icon: Building2, color: "text-blue-600", bg: "bg-blue-50", to: "/organisasjoner" },
    { label: "Brukere", value: overview?.users, icon: Users, color: "text-violet-600", bg: "bg-violet-50", to: "/brukere" },
    { label: "Aktive auksjoner", value: overview?.activeAuctions, icon: Gavel, color: "text-accent", bg: "bg-accent/8", to: "/auksjoner" },
    { label: "Ventende oppgaver", value: overview?.openTasks, icon: ListTodo, color: "text-amber-600", bg: "bg-amber-50", to: "/oppgaver" },
  ];

  const attentionItems = [
    { label: "Auksjoner som venter godkjenning", count: overview?.pendingAuctionApprovals, to: "/oppgaver" },
    { label: "Organisasjoner som venter godkjenning", count: overview?.pendingOrgRegistrations, to: "/oppgaver" },
    { label: "Åpne supporthenvendelser", count: overview?.openSupportTickets, to: "/support" },
  ];

  return (
    <div className="px-4 py-3 sm:py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: "var(--font-display)" }}>
          Dashboard
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">Overblikk over plattformen</p>
      </div>

      <PushNotificationBanner />

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {stats.map(({ label, value, icon: Icon, color, bg, to }) => (
          <Card key={label} className="p-4 sm:p-5 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate(to)}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-medium text-muted-foreground">{label}</p>
                <p className="mt-1 text-2xl font-black tracking-tight text-foreground">
                  {loading ? "..." : value?.toLocaleString("nb-NO") ?? "—"}
                </p>
              </div>
              <div className={`flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0 ${bg}`}>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
        {/* Recent tasks */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Siste oppgaver</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="divide-y">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-14 px-6 flex items-center">
                    <div className="h-3 w-2/3 rounded bg-muted/50 animate-pulse" />
                  </div>
                ))}
              </div>
            ) : overview?.recentTasks.length === 0 ? (
              <p className="px-6 py-4 text-sm text-muted-foreground">Ingen oppgaver ennå.</p>
            ) : (
              <div className="divide-y divide-border">
                {overview?.recentTasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center gap-3 px-4 sm:px-6 py-3.5 hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => navigate(task.taskType === "approval_review" ? `/auksjoner/${task.id}` : `/oppgaver/${task.id}`)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground break-words">{task.title}</p>
                      <p className="text-xs text-muted-foreground">{taskTypeLabel(task.taskType, task.module)}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(task.createdAt)}</span>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Needs attention */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Krever oppmerksomhet</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {attentionItems.map(({ label, count, to }) => (
                <div
                  key={label}
                  className="flex items-center justify-between px-4 sm:px-6 py-3.5 hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => navigate(to)}
                >
                  <span className="text-sm text-foreground pr-2">{label}</span>
                  <span className={`text-sm font-bold tabular-nums ${!loading && count && count > 0 ? "text-amber-600" : "text-muted-foreground"}`}>
                    {loading ? "..." : count?.toLocaleString("nb-NO") ?? "—"}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
